import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, ArrowRight, BarChart2, BookOpen, Brain, CheckCircle2,
  ChevronLeft,
  FileText, GitBranch, HelpCircle, History,
  Layers, Lightbulb,
  Maximize2, Moon, Network, PanelLeftClose,
  PanelLeftOpen, PanelRightClose, PanelRightOpen,
  Sun, Target,
  TrendingUp,
  X, Zap
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { createPortal } from 'react-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import ClientView from '../components/Simulations/ClientView';
import CommercialOverlay from '../components/Simulations/CommercialOverlay';
import RapportEnquete from '../components/Simulations/RapportEnquete';
import api from '../config/axiosConfig';
import { EVENT_TYPES, useCommercialStream } from '../hooks/useCommercialStream';
import { usePesa } from '../stores/usePesaEngine';
import { LiveFieldModal, PESAField } from './GenericCase';

const THEMES = {
  light: {
    bg: 'bg-[#f8fafc]', bgHex: '#f8fafc',
    surface: 'bg-white/80', surfaceHex: '#FFFFFF',
    border: 'border-[#e2e8f0]', borderHex: '#e2e8f0',
    text: 'text-[#0f172a]', textMuted: 'text-[#64748b]',
    accent: '#f59e0b', accentText: 'text-[#f59e0b]', accentBg: 'bg-[#f59e0b]/10',
    ink: '#0f172a',
    panelBg: 'rgba(255,255,255,0.88)',
    panelBorder: 'rgba(245,158,11,0.15)',
    gridLine: 'rgba(15,23,42,0.04)',
    radarBg: 'rgba(245,158,11,0.04)', radarBorder: '#f59e0b',
    headerBg: 'rgba(248,250,252,0.96)',
  },
  dark: {
    bg: 'bg-[#020617]', bgHex: '#020617',
    surface: 'bg-[#0f172a]/90', surfaceHex: '#0f172a',
    border: 'border-[#1e293b]', borderHex: '#1e293b',
    text: 'text-[#f1f5f9]', textMuted: 'text-[#94a3b8]',
    accent: '#f59e0b', accentText: 'text-[#f59e0b]', accentBg: 'bg-[#f59e0b]/10',
    ink: '#f1f5f9',
    panelBg: 'rgba(15,23,42,0.92)',
    panelBorder: 'rgba(245,158,11,0.12)',
    gridLine: 'rgba(255,255,255,0.025)',
    radarBg: 'rgba(245,158,11,0.04)', radarBorder: '#f59e0b',
    headerBg: 'rgba(2,6,23,0.95)',
  },
};

const VOCAB_MAPS = {
  vente: {
    pedagogicalGoal:    { label: 'Proposition de valeur',      icon: Target    },
    steps:              { label: 'Phases de la proposition',   icon: Layers    },
    action:             { label: 'Décisions stratégiques',     icon: Zap       },
    solutions:          { label: 'Scénarios de résultat',      icon: BarChart2 },
    counterfactuals:    { label: 'Et si vous aviez choisi…',   icon: GitBranch },
    causality:          { label: "Chaîne d'impact",            icon: Network   },
    prediction:         { label: 'Probabilité de succès',      icon: TrendingUp},
    history:            { label: 'Journal de décisions',       icon: History   },
    dynamics:           { label: 'Dynamiques en jeu',          icon: Activity  },
    theory:             { label: 'Références & Benchmark',     icon: BookOpen  },
    parameters:         { label: 'Indicateurs de performance', icon: BarChart2 },
    whatHappened:       { label: 'Ce que votre client perçoit'           },
    whyItMatters:       { label: 'Pourquoi ça décide'                    },
    whatItCosts:        { label: 'Le risque si vous ne le faites pas'    },
    boostCondition:     { label: 'Le levier suivant'                     },
    competencySignal:   { label: 'Signal de confiance client'            },
    currentSolution:    { label: 'Scénario activé'                       },
    stabilite:          { label: 'Stabilité du projet'                   },
    confiance:          { label: 'Confiance client'                      },
    ethique:            { label: "Intégrité de l'offre"                  },
    equite:             { label: 'Équité de valeur'                      },
    mediation:          { label: 'Capacité de dialogue'                  },
    innovation:         { label: 'Innovation'                            },
    santePublique:      { label: 'Impact marché'                         },
    sante:              { label: 'Santé du projet'                       },
  },
  pedagogique: {
    pedagogicalGoal:    { label: 'Objectif pédagogique',    icon: Brain      },
    steps:              { label: 'Étapes',                  icon: Layers     },
    action:             { label: 'Actions',                 icon: Zap        },
    solutions:          { label: 'Solutions',               icon: CheckCircle2},
    counterfactuals:    { label: 'Contrefactuels',          icon: GitBranch  },
    causality:          { label: 'Graphe causal',           icon: Network    },
    prediction:         { label: 'Prédictions',             icon: Lightbulb  },
    history:            { label: 'Journal',                 icon: History    },
    dynamics:           { label: 'Dynamiques',              icon: Activity   },
    theory:             { label: 'Théories',                icon: Brain      },
    parameters:         { label: 'Métriques système',       icon: BarChart2  },
    whatHappened:       { label: "Ce qui s'est passé"                    },
    whyItMatters:       { label: 'Pourquoi ça compte'                    },
    whatItCosts:        { label: 'Ce que ça coûte'                       },
    boostCondition:     { label: 'Levier suivant'                        },
    competencySignal:   { label: 'Compétence'                            },
    currentSolution:    { label: 'Solution atteinte'                     },
  },
};

const useVocab = (scenario) => {
  const mode = scenario?.displayMode || 'vente';
  const map  = VOCAB_MAPS[mode] || VOCAB_MAPS.vente;
  return (key) => map[key] || { label: key };
};

const useCommercialTheme = (scenario) => {
  const [themeKey, setThemeKey] = useState(scenario?.theme === 'light' ? 'light' : 'dark');
  const theme  = THEMES[themeKey] || THEMES.dark;
  const toggle = () => setThemeKey(k => k === 'light' ? 'dark' : 'light');
  return { theme, themeKey, toggle };
};

const Tag = ({ children, variant = 'gold', theme }) => {
  const variants = {
    gold:  { bg: `${theme.accent}18`, border: `${theme.accent}35`, color: theme.accent },
    green: { bg: '#10b98115',         border: '#10b98130',         color: '#10b981'    },
    red:   { bg: '#ef444415',         border: '#ef444430',         color: '#ef4444'    },
    slate: { bg: `${theme.ink}08`,    border: `${theme.ink}15`,    color: `${theme.ink}55` },
  };
  const s = variants[variant] || variants.gold;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[8px] font-mono tracking-[0.2em] uppercase"
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>
      {children}
    </span>
  );
};

const Mono = ({ children, style = {} }) => (
  <span className="font-mono text-[9px] tracking-[0.18em] uppercase" style={style}>{children}</span>
);

const CommercialHeader = ({
  scenario, phase, progress, theme, themeKey,
  onToggleTheme, onRestart, presentationMode, setPresentationMode, navigate,
}) => {
  const stepLabel = phase === 'finished' ? 'Terminé' : phase === 'playing' ? 'En cours' : phase;

  return (
    <header className="shrink-0 sticky top-0 z-[60] backdrop-blur-2xl"
      style={{ background: theme.headerBg, borderBottom: `1px solid ${theme.borderHex}` }}>
      <div className="max-w-[1920px] mx-auto px-6 h-14 flex items-center gap-5">

        {/* Retour */}
        <button onClick={() => navigate('/dashboard')}
          className="shrink-0 flex items-center gap-2 group transition-all"
          style={{ color: `${theme.ink}35` }}
          onMouseEnter={e => e.currentTarget.style.color = theme.ink}
          onMouseLeave={e => e.currentTarget.style.color = `${theme.ink}35`}>
          <ChevronLeft size={15} />
          <Mono style={{ color: 'inherit', fontSize: '8px' }}>Retour</Mono>
        </button>

        <div className="h-4 w-px" style={{ background: `${theme.ink}10` }} />

        {/* Titre */}
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <Tag variant="gold" theme={theme}>Simulation</Tag>
          <h1 className="text-[13px] font-semibold tracking-tight truncate"
            style={{ color: theme.ink, fontFamily: '"DM Sans", sans-serif', letterSpacing: '-0.02em' }}>
            {scenario?.title}
          </h1>
        </div>

        {/* Progression centrale */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                style={{ background: phase === 'playing' ? '#10b981' : theme.accent }} />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5"
                style={{ background: phase === 'playing' ? '#10b981' : theme.accent }} />
            </div>
            <Mono style={{ color: phase === 'playing' ? '#10b981' : theme.accent }}>{stepLabel}</Mono>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-32 h-1 rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
              <motion.div className="h-full rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: 'circOut' }}
                style={{ background: `linear-gradient(90deg, ${theme.accent}, #10b981)` }} />
            </div>
            <Mono style={{ color: `${theme.ink}35`, minWidth: '2rem', textAlign: 'right' }}>{progress}%</Mono>
          </div>
        </div>



        {/* Actions droite */}
        <div className="flex items-center gap-2">
          {/* Mode toggle */}
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg"
            style={{ background: `${theme.ink}06`, border: `1px solid ${theme.ink}08` }}>
            {[{ id: 'solo', label: 'Solo' }, { id: 'live', label: 'Live' }].map(({ id, label }) => (
              <button key={id} onClick={() => setPresentationMode(id)}
                className="px-3 py-1.5 rounded-md text-[9px] font-mono tracking-widest uppercase transition-all"
                style={{
                  background: presentationMode === id ? theme.accent : 'transparent',
                  color: presentationMode === id ? '#fff' : `${theme.ink}35`,
                }}>
                {label}
              </button>
            ))}
          </div>

          <button onClick={onToggleTheme}
            className="p-2 rounded-lg transition-all"
            style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}08` }}>
            {themeKey === 'dark'
              ? <Sun size={13} style={{ color: `${theme.ink}40` }} />
              : <Moon size={13} style={{ color: `${theme.ink}40` }} />}
          </button>

          

          <button onClick={onRestart}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-mono tracking-widest uppercase transition-all"
            style={{ background: '#ef444408', color: '#ef444470', border: '1px solid #ef444418' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#ef444415'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#ef444408'; e.currentTarget.style.color = '#ef444470'; }}>
            <History size={11} />Réinitialiser
          </button>


        </div>
      </div>
    </header>
  );
};

const PredictionPanel = ({ theme, V }) => {
  const { engine, phase, currentSolution } = usePesa();
  const [showInfo, setShowInfo] = useState(false);

  let proba = 0;
  if (phase === 'finished' && currentSolution?.interpretation) {
    proba = currentSolution.interpretation.realWorldProbability ?? currentSolution.interpretation.theoreticalPromise ?? 10;
  } else {
    proba = engine?.state?.optimalPath?.probabilityOfSuccess ?? engine?.state?.prediction?.probability ?? 0;
  }
  const pct = Math.max(0, Math.min(100, proba > 1 ? proba : proba * 100));

  const status =
    pct >= 80 ? { label: 'Excellent', color: '#10b981' } :
    pct >= 60 ? { label: 'Favorable', color: theme.accent } :
    pct >= 40 ? { label: 'Incertain', color: '#f59e0b' } :
                { label: 'Critique',  color: '#ef4444' };

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-end justify-between">
        <div>
          <Mono style={{ color: `${theme.ink}30`, display: 'block', marginBottom: '6px' }}>
            {V('prediction').label}
          </Mono>
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold font-mono tabular-nums"
              style={{ color: theme.ink, letterSpacing: '-0.04em' }}>
              {pct.toFixed(0)}
            </span>
            <span className="text-lg font-mono" style={{ color: `${theme.ink}25` }}>%</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Tag variant={pct >= 60 ? 'green' : pct >= 40 ? 'gold' : 'red'} theme={theme}>
            {status.label}
          </Tag>
          <button onClick={() => setShowInfo(true)}
            className="transition-opacity hover:opacity-70"
            style={{ color: `${theme.ink}20` }}>
            <HelpCircle size={10} />
          </button>
        </div>
      </div>

      <div className="relative h-1 rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
        <motion.div className="absolute inset-y-0 left-0 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          style={{ background: `linear-gradient(90deg, ${status.color}80, ${status.color})` }} />
      </div>

      <Mono style={{ color: `${theme.ink}18`, fontSize: '7px' }}>
        {phase === 'finished' ? 'Données historiques 1900–2025' : 'Optimisation génétique PESA'}
      </Mono>

      {createPortal(
        <AnimatePresence>
          {showInfo && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
              style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)' }}
              onClick={() => setShowInfo(false)}>
              <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 10 }}
                onClick={e => e.stopPropagation()}
                className="max-w-sm w-full rounded-2xl p-6"
                style={{ background: theme.surfaceHex, border: `1px solid ${theme.panelBorder}` }}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: theme.ink }}>Calibration bayésienne</p>
                    <Mono style={{ color: theme.accent, marginTop: '2px' }}>Moteur PESA</Mono>
                  </div>
                  <button onClick={() => setShowInfo(false)} style={{ color: `${theme.ink}30` }}><X size={14} /></button>
                </div>
                <p className="text-[12px] leading-relaxed" style={{ color: `${theme.ink}55` }}>
                  Ce pourcentage représente la <strong style={{ color: theme.ink }}>fréquence historique réelle</strong> de trajectoires similaires (1900–2025) — pas une promesse.
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>, document.body
      )}
    </div>
  );
};

const HistoryPanel = ({ theme, V }) => {
  const { history } = usePesa();
  const reversed = [...(history || [])].reverse().slice(0, 8);
  return (
    <div className="max-h-[220px] overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
      {reversed.length === 0 ? (
        <div className="py-6 text-center">
          <Mono style={{ color: `${theme.ink}18` }}>Aucune décision</Mono>
        </div>
      ) : (
        <div className="p-3 space-y-1">
          {reversed.map((h, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center gap-3 px-3 py-2 rounded-lg"
              style={{ background: `${theme.ink}03` }}>
              <div className="w-0.5 h-full self-stretch rounded-full" style={{ background: `${theme.accent}30` }} />
              <div className="flex-1 min-w-0">
                <Mono style={{ color: `${theme.accent}50`, fontSize: '7px' }}>{h.type}</Mono>
                <p className="text-[9px] truncate mt-0.5" style={{ color: `${theme.ink}40` }}>
                  {h.actionId || '—'}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

const SolutionPanel = ({ theme, V }) => {
  const { currentSolution, phase } = usePesa();
  const loading = phase === 'playing' || phase === 'loading';

  return (
    <div className="p-4">
      {loading ? (
        <div className="space-y-2">
          <Mono style={{ color: `${theme.ink}25` }}>Calcul…</Mono>
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-0.5 flex-1 rounded-full animate-pulse"
                style={{ background: `${theme.accent}25`, animationDelay: `${i * 80}ms` }} />
            ))}
          </div>
        </div>
      ) : currentSolution ? (
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={12} style={{ color: '#10b981', marginTop: '2px' }} className="shrink-0" />
            <div>
              <p className="text-[12px] font-semibold leading-tight" style={{ color: theme.ink }}>
                {currentSolution.name}
              </p>
              <p className="text-[10px] leading-relaxed mt-1" style={{ color: `${theme.ink}45` }}>
                {currentSolution.description}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="px-2.5 py-2 rounded-lg" style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}06` }}>
              <Mono style={{ color: `${theme.ink}25`, fontSize: '7px', display: 'block', marginBottom: '2px' }}>Complexité</Mono>
              <p className="text-[10px] font-mono" style={{ color: `${theme.ink}55` }}>{currentSolution.difficulty || '—'}</p>
            </div>
            <div className="px-2.5 py-2 rounded-lg" style={{ background: '#10b98106', border: '1px solid #10b98118' }}>
              <Mono style={{ color: `${theme.ink}25`, fontSize: '7px', display: 'block', marginBottom: '2px' }}>Fiabilité</Mono>
              <p className="text-[10px] font-mono font-bold" style={{ color: '#10b981' }}>94%</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 py-2">
          <Activity size={10} className="animate-pulse shrink-0" style={{ color: `${theme.ink}25` }} />
          <Mono style={{ color: `${theme.ink}25` }}>Non encore atteint</Mono>
        </div>
      )}
    </div>
  );
};

const CounterfactualsPanel = ({ counterfactuals = [], theme, V }) => {
  const [open, setOpen] = useState(false);
  if (!counterfactuals?.length) return null;
  const top = [...counterfactuals].sort((a, b) => b.deltaProbability - a.deltaProbability).slice(0, 3);

  return (
    <div className="p-4 space-y-3">
      {top.map((cf, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="flex-shrink-0 h-5 flex items-center px-2 rounded"
            style={{ background: `${theme.accent}10`, border: `1px solid ${theme.accent}20`, minWidth: `${28 + cf.deltaProbability * 40}px` }}>
            <span className="text-[8px] font-mono font-bold whitespace-nowrap" style={{ color: theme.accent }}>
              +{(cf.deltaProbability * 100).toFixed(0)}%
            </span>
          </div>
          <p className="text-[10px] truncate" style={{ color: `${theme.ink}45` }}>{cf.actionTitle}</p>
        </div>
      ))}
      <button onClick={() => setOpen(true)}
        className="w-full py-1.5 rounded-lg text-[8px] font-mono tracking-widest uppercase transition-all"
        style={{ border: `1px solid ${theme.ink}08`, color: `${theme.ink}25` }}
        onMouseEnter={e => { e.currentTarget.style.color = `${theme.ink}55`; e.currentTarget.style.borderColor = `${theme.ink}15`; }}
        onMouseLeave={e => { e.currentTarget.style.color = `${theme.ink}25`; e.currentTarget.style.borderColor = `${theme.ink}08`; }}>
        Analyse complète →
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)' }}
              onClick={() => setOpen(false)}>
              <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
                onClick={e => e.stopPropagation()}
                className="max-w-lg w-full max-h-[80vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: theme.surfaceHex, border: `1px solid ${theme.panelBorder}` }}>
                <div className="px-5 py-4 flex justify-between items-center shrink-0"
                  style={{ borderBottom: `1px solid ${theme.borderHex}` }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: theme.ink }}>{V('counterfactuals').label}</p>
                    <Mono style={{ color: theme.accent, marginTop: '2px' }}>Bifurcation Engine</Mono>
                  </div>
                  <button onClick={() => setOpen(false)} style={{ color: `${theme.ink}30` }}><X size={14} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {counterfactuals.map((cf, i) => (
                    <div key={i} className="p-4 rounded-xl"
                      style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Tag variant="gold" theme={theme}>+{(cf.deltaProbability * 100).toFixed(1)}%</Tag>
                        <p className="text-[11px] font-medium" style={{ color: theme.ink }}>{cf.actionTitle}</p>
                      </div>
                      <p className="text-[11px] leading-relaxed" style={{ color: `${theme.ink}45` }}>{cf.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>, document.body
      )}
    </div>
  );
};

const DynamicsPanel = ({ theme, V }) => {
  const { engine } = usePesa();
  const dynamics   = engine?.case?.simulationEngine?.dynamics || [];
  const tick       = engine?.currentTick || 0;
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setProgress(p => (p + 6) % 100), 500);
    return () => clearInterval(id);
  }, []);

  if (!dynamics.length) return null;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <Mono style={{ color: `${theme.ink}25`, display: 'block', marginBottom: '6px' }}>
            Cycle de simulation
          </Mono>
          <div className="h-0.5 rounded-full overflow-hidden" style={{ background: `${theme.ink}08` }}>
            <motion.div className="h-full rounded-full"
              style={{ width: `${progress}%`, background: theme.accent }}
              transition={{ duration: 0.3 }} />
          </div>
        </div>
        <span className="text-xs font-mono font-bold ml-4 tabular-nums" style={{ color: theme.accent }}>
          T{String(tick).padStart(3, '0')}
        </span>
      </div>
      <div className="flex flex-wrap gap-1">
        {dynamics.slice(0, 5).map((d, i) => (
          <Tag key={i} variant="gold" theme={theme}>{d.type || 'Processus'}</Tag>
        ))}
      </div>
    </div>
  );
};

const CausalPanel = ({ theme, V }) => {
  const [tab, setTab]     = useState('impact');
  const [open, setOpen]   = useState(false);
  const { engine, completedActions } = usePesa();

  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  const lastActionId = completedArr[completedArr.length - 1] || null;
  const lastAction   = useMemo(() => {
    if (!lastActionId) return null;
    return engine?.case?.steps?.flatMap(s => s.actions || []).find(a => a.id === lastActionId) || null;
  }, [lastActionId, engine?.case?.steps]);

  const currentStep  = engine?.getCurrentStep?.();
  const causalities  = [...(currentStep?.causality || []), ...(engine?.case?.simulationEngine?.causality || [])].slice(0, 5);
  const df           = lastAction?.deepFeedback;
  const cc           = lastAction?.causalChain || [];
  const comp         = lastAction?.competencySignal;
  const hasFeedback  = df && (df.whatHappened || df.whyItMatters || df.whatItCosts);

  const tabs = [
    { id: 'impact',   label: 'Impact',   disabled: false },
    { id: 'feedback', label: 'Analyse',  disabled: !hasFeedback },
    { id: 'signal',   label: 'Signal',   disabled: !comp },
  ];

  const Content = () => (
    <div className="space-y-3 p-4">
      {lastAction && (
        <div className="flex items-center gap-1.5">
          <div className="w-1 h-1 rounded-full" style={{ background: theme.accent }} />
          <Mono style={{ color: `${theme.accent}50`, fontSize: '7px' }}>↳ {lastAction.id}</Mono>
        </div>
      )}
      <div className="flex gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => !t.disabled && setTab(t.id)} disabled={t.disabled}
            className="flex-1 py-1.5 rounded-lg text-[8px] font-mono tracking-widest uppercase transition-all border"
            style={{
              opacity: t.disabled ? 0.2 : 1,
              cursor: t.disabled ? 'not-allowed' : 'pointer',
              background: tab === t.id ? `${theme.accent}10` : 'transparent',
              borderColor: tab === t.id ? `${theme.accent}25` : `${theme.ink}08`,
              color: tab === t.id ? theme.accent : `${theme.ink}30`,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === 'impact' && (
          <motion.div key="impact" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="space-y-1.5">
            {causalities.length > 0 ? causalities.map((c, i) => (
              <div key={i} className="px-3 py-2 rounded-lg"
                style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}05` }}>
                <div className="flex items-center gap-2 text-[9px] mb-1">
                  <span className="flex-1 truncate" style={{ color: `${theme.ink}45` }}>{c.cause}</span>
                  <ArrowRight size={7} style={{ color: `${theme.ink}18` }} />
                  <span className="flex-1 text-right truncate font-medium"
                    style={{ color: (c.weight || 0) > 0 ? '#10b981' : '#ef4444' }}>{c.effect}</span>
                </div>
                <div className="h-0.5 rounded-full overflow-hidden" style={{ background: `${theme.ink}06` }}>
                  <div className="h-full rounded-full"
                    style={{ width: `${Math.abs((c.weight || 0) * 100)}%`, background: (c.weight || 0) > 0 ? '#10b981' : '#ef4444', opacity: 0.6 }} />
                </div>
              </div>
            )) : (
              <p className="text-center py-4 text-[9px] font-mono italic" style={{ color: `${theme.ink}18` }}>
                Aucun impact actif
              </p>
            )}
          </motion.div>
        )}

        {tab === 'feedback' && hasFeedback && (
          <motion.div key="feedback" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="space-y-2">
            {[
              { key: 'whatHappened', color: `${theme.ink}60` },
              { key: 'whyItMatters', color: theme.accent },
              { key: 'whatItCosts',  color: '#ef4444' },
            ].filter(({ key }) => df?.[key]).map(({ key, color }) => (
              <div key={key} className="p-2.5 rounded-lg"
                style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
                <Mono style={{ color: `${theme.ink}22`, display: 'block', marginBottom: '4px' }}>
                  {V(key).label}
                </Mono>
                <p className="text-[10px] leading-relaxed" style={{ color }}>{df[key]}</p>
              </div>
            ))}
            {df?.boostCondition && (
              <div className="p-2.5 rounded-lg" style={{ background: '#10b98106', border: '1px solid #10b98118' }}>
                <Mono style={{ color: '#10b98160', display: 'block', marginBottom: '4px' }}>
                  {V('boostCondition').label}
                </Mono>
                <p className="text-[10px] italic" style={{ color: '#10b98175' }}>{df.boostCondition}</p>
              </div>
            )}
          </motion.div>
        )}

        {tab === 'signal' && comp && (
          <motion.div key="signal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="p-3 rounded-xl"
              style={{ background: `${theme.accent}08`, border: `1px solid ${theme.accent}18` }}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="text-[11px] font-semibold" style={{ color: theme.ink }}>{comp.name}</p>
                <Tag variant="gold" theme={theme}>{comp.level}</Tag>
              </div>
              <p className="text-[10px] leading-relaxed" style={{ color: `${theme.ink}45` }}>{comp.descriptor}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <>
      <Content />
      <div className="px-4 pb-4">
        <button onClick={() => setOpen(true)}
          className="w-full py-1.5 rounded-lg text-[8px] font-mono tracking-widest uppercase transition-all"
          style={{ border: `1px solid ${theme.ink}08`, color: `${theme.ink}22` }}
          onMouseEnter={e => { e.currentTarget.style.color = `${theme.ink}50`; e.currentTarget.style.borderColor = `${theme.ink}15`; }}
          onMouseLeave={e => { e.currentTarget.style.color = `${theme.ink}22`; e.currentTarget.style.borderColor = `${theme.ink}08`; }}>
          Vue étendue →
        </button>
      </div>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)' }}
              onClick={() => setOpen(false)}>
              <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96 }}
                onClick={e => e.stopPropagation()}
                className="max-w-lg w-full max-h-[80vh] overflow-hidden rounded-2xl flex flex-col"
                style={{ background: theme.surfaceHex, border: `1px solid ${theme.panelBorder}` }}>
                <div className="px-5 py-4 flex items-center justify-between shrink-0"
                  style={{ borderBottom: `1px solid ${theme.borderHex}` }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: theme.ink }}>{V('causality').label}</p>
                    <Mono style={{ color: theme.accent, marginTop: '2px' }}>Vue complète</Mono>
                  </div>
                  <button onClick={() => setOpen(false)} style={{ color: `${theme.ink}30` }}><X size={14} /></button>
                </div>
                <div className="flex-1 overflow-y-auto"><Content /></div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>, document.body
      )}
    </>
  );
};

const ROIPanel = ({ theme, V, scenario }) => {
  const { currentSolution, phase, currentCase, parameters, initialParameters } = usePesa();

  const displayValues = useMemo(() => {
    if (Object.keys(parameters).length > 0 && Object.values(parameters).every(v => typeof v === 'number' && !isNaN(v)))
      return { ...parameters };
    return { ...initialParameters };
  }, [parameters, initialParameters]);

  const { keys, values, labels } = useMemo(() => {
    const k = Object.keys(displayValues).filter(k => typeof displayValues[k] === 'number' && !isNaN(displayValues[k]));
    return {
      keys: k,
      values: k.map(k => displayValues[k] ?? 50),
      labels: k.map(k => {
        const mapped = V(k);
        return mapped?.label || k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
      }),
    };
  }, [displayValues, V]);

  const deltaInfo = useMemo(() => {
    if (!currentSolution?.expectedMetrics || phase !== 'finished') return null;
    const expected = currentSolution.expectedMetrics;
    const valid    = keys.filter(k => expected[k] !== undefined);
    if (!valid.length) return null;
    const avgE = Math.round(valid.reduce((s, k) => s + (expected[k] || 0), 0) / valid.length);
    const avgC = Math.round(valid.reduce((s, k) => s + (displayValues[k] || 50), 0) / valid.length);
    return { delta: avgC - avgE, avgExpected: avgE, avgCurrent: avgC };
  }, [currentSolution, phase, keys, displayValues]);

  const status = !deltaInfo
    ? { label: 'En cours',        color: theme.accent  }
    : deltaInfo.delta >= 10
    ? { label: 'Surperformance',  color: '#10b981'     }
    : deltaInfo.delta < 0
    ? { label: 'Sous objectif',   color: '#ef4444'     }
    : { label: 'Dans les clous',  color: theme.accent  };

  const radarData = useMemo(() => ({
    labels,
    datasets: [{
      label: 'Spectre',
      data: values,
      backgroundColor: `${status.color}06`,
      borderColor: status.color,
      borderWidth: 1.5,
      pointBackgroundColor: status.color,
      pointBorderColor: 'rgba(255,255,255,0.2)',
      pointBorderWidth: 1,
      pointRadius: 2.5,
      pointHoverRadius: 4,
      tension: 0.3,
    }],
  }), [labels, values, status.color]);

  const radarOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: true,
    animation: { duration: 600, easing: 'easeOutQuart' },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: theme.surfaceHex,
        titleFont: { family: '"DM Mono", monospace', size: 9 },
        bodyFont:  { family: '"DM Mono", monospace', size: 9 },
        padding: 8,
        borderColor: theme.panelBorder,
        borderWidth: 1,
        titleColor: theme.ink,
        bodyColor: theme.ink,
        callbacks: { label: ctx => ` ${ctx.parsed.r}%` },
      },
    },
    scales: {
      r: {
        angleLines: { color: `${theme.ink}05` },
        grid: { color: `${theme.ink}06`, lineWidth: 0.5 },
        pointLabels: { color: `${theme.ink}45`, font: { size: 8, family: '"DM Mono", monospace' }, padding: 6 },
        ticks: { display: false, stepSize: 25 },
        min: 0, max: 100, beginAtZero: true,
      },
    },
  }), [theme]);

  if (!values.length) return null;

  return (
    <div className="rounded-2xl overflow-hidden"
      style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}` }}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <Mono style={{ color: theme.accent, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <BarChart2 size={9} style={{ display: 'inline' }} />
              {' '}{V('parameters').label}
            </Mono>
            <p className="text-[12px] font-semibold" style={{ color: theme.ink, fontFamily: '"DM Sans", sans-serif' }}>
              Analyse Spectrale
            </p>
          </div>
          <Mono style={{ color: `${theme.ink}30`, background: `${theme.ink}05`, border: `1px solid ${theme.ink}08`, padding: '2px 6px', borderRadius: '4px' }}>
            {currentCase?.slug?.toUpperCase() || '—'}
          </Mono>
        </div>

        {/* Radar */}
        <div className="flex justify-center mb-3">
          <div className="w-full max-w-[240px]">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center justify-between px-3 py-2 rounded-xl mb-3"
          style={{ background: `${theme.ink}03`, border: `1px solid ${theme.ink}06` }}>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: status.color }} />
            <div>
              <Mono style={{ color: `${theme.ink}22`, fontSize: '7px', display: 'block' }}>Statut</Mono>
              <Mono style={{ color: status.color, fontSize: '9px', fontWeight: '700' }}>{status.label}</Mono>
            </div>
          </div>
          <div className="text-right">
            <Mono style={{ color: `${theme.ink}22`, fontSize: '7px', display: 'block' }}>Variance</Mono>
            <span className="text-xl font-bold font-mono tabular-nums"
              style={{ color: deltaInfo?.delta < 0 ? '#ef4444' : '#10b981', letterSpacing: '-0.04em' }}>
              {deltaInfo ? `${deltaInfo.delta > 0 ? '+' : ''}${deltaInfo.delta}%` : '—'}
            </span>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-1.5">
          {keys.slice(0, 8).map((k, i) => {
            const val    = displayValues[k] ?? 0;
            const pct    = Math.min(val, 100);
            const initV  = initialParameters?.[k];
            const delta  = initV !== undefined ? val - initV : null;
            const dColor = delta === null ? '' : delta > 0 ? '#10b981' : delta < 0 ? '#ef4444' : `${theme.ink}30`;
            const dSym   = delta === null ? '' : delta > 0 ? '↑' : delta < 0 ? '↓' : '•';

            return (
              <div key={k} className="relative overflow-hidden rounded-lg"
                style={{ background: `${theme.ink}04`, border: `1px solid ${theme.ink}06` }}>
                <div className="absolute inset-y-0 left-0 rounded-l-lg"
                  style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${theme.accent}20, ${theme.accent}08)`, transition: 'width 0.8s cubic-bezier(0.2,0.9,0.4,1.1)' }} />
                <div className="relative flex items-center justify-between gap-1 px-2.5 py-2">
                  <div className="flex items-center gap-1 overflow-hidden">
                    <Mono style={{ color: `${theme.ink}50`, fontSize: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80px' }}>
                      {labels[i]}
                    </Mono>
                    {delta !== null && (
                      <span className="text-[7px] font-mono tabular-nums" style={{ color: dColor }}>
                        {dSym}{Math.abs(Math.round(delta))}%
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] font-mono font-bold tabular-nums shrink-0" style={{ color: theme.ink }}>
                    {val}%
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Cible */}
        {currentSolution && phase === 'finished' && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl"
            style={{ background: `${theme.accent}06`, border: `1px dashed ${theme.accent}25` }}>
            <Target size={10} style={{ color: theme.accent }} />
            <div>
              <Mono style={{ color: `${theme.ink}25`, fontSize: '7px', display: 'block' }}>Cible atteinte</Mono>
              <Mono style={{ color: theme.accent, fontWeight: '700' }}>{currentSolution.name}</Mono>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const NavButton = ({ active, icon: Icon, label, onClick, pulsing, theme }) => (
  <button onClick={onClick}
    className="relative group flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-[9px] font-mono tracking-widest uppercase transition-all duration-200 border"
    style={{
      background: active ? `${theme.accent}10` : 'transparent',
      borderColor: active ? `${theme.accent}28` : `${theme.ink}08`,
      color: active ? theme.accent : `${theme.ink}30`,
    }}
    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = `${theme.ink}04`; e.currentTarget.style.color = `${theme.ink}55`; }}}
    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = `${theme.ink}30`; }}}>
    {pulsing && (
      <span className="absolute inset-0 rounded-xl animate-ping"
        style={{ border: `1px solid ${theme.accent}40`, animationDuration: '0.9s', animationIterationCount: 3 }} />
    )}
    <Icon size={12} />
    <span className="flex-1 text-left">{label}</span>
    {pulsing && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: theme.accent }} />}
  </button>
);

const SectionHeader = ({ label, icon: Icon, theme }) => (
  <div className="flex items-center gap-2 px-4 pt-4 pb-2">
    {Icon && <Icon size={10} style={{ color: theme.accent }} />}
    <Mono style={{ color: theme.accent }}>{label}</Mono>
  </div>
);

const PanelCard = ({ children, theme }) => (
  <motion.div layout
    initial={{ opacity: 0, y: -4 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -4 }}
    transition={{ duration: 0.18, ease: 'easeOut' }}
    className="rounded-2xl overflow-hidden backdrop-blur-md"
    style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}` }}>
    {children}
  </motion.div>
);

const LoadingScreen = ({ theme }) => (
  <div className="fixed inset-0 z-[1000] flex items-center justify-center" style={{ background: theme.bgHex }}>
    <div className="flex flex-col items-center gap-6">
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
        className="w-14 h-14 rounded-full"
        style={{ border: `3px solid ${theme.accent}20`, borderTopColor: theme.accent }} />
      <Mono style={{ color: `${theme.ink}30` }}>Initialisation PESA…</Mono>
    </div>
  </div>
);

const PitchScreen = ({ scenario, theme, V, presentationMode, setPresentationMode, onStart, liveLink, setLiveLink, onGenerateLiveToken }) => {
  useEffect(() => {
    if (presentationMode === 'live' && !liveLink) {
      onGenerateLiveToken();
    }
  }, [presentationMode]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full mx-4"
      >
        <div className="rounded-2xl p-8 shadow-2xl"
          style={{ background: theme.panelBg, border: `1px solid ${theme.panelBorder}` }}>
          
          <h2 className="text-xl font-light mb-2" style={{ color: theme.ink }}>
            {scenario?.title || 'Simulation commerciale'}
          </h2>
          <p className="text-sm mb-6" style={{ color: theme.inkMuted }}>
            {V('pedagogicalGoal')?.label || 'Présentation interactive'}
          </p>

          {/* Choix du mode */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setPresentationMode('solo')}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                presentationMode === 'solo'
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'bg-transparent border border-zinc-300 text-zinc-600 hover:border-amber-500'
              }`}
            >
              Mode Solo (vous)
            </button>
            <button
              onClick={() => setPresentationMode('live')}
              className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                presentationMode === 'live'
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'bg-transparent border border-zinc-300 text-zinc-600 hover:border-amber-500'
              }`}
            >
              Mode Live (client)
            </button>
          </div>

          {/* Lien live */}
          {presentationMode === 'live' && liveLink && (
            <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-600 mb-2">Lien à partager avec votre client :</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={liveLink}
                  className="flex-1 bg-transparent border-b border-amber-500/30 text-sm py-1 outline-none"
                  style={{ color: theme.ink }}
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(liveLink);
                    toast.success('Lien copié !');
                  }}
                  className="px-3 py-1 rounded bg-amber-500 text-white text-xs"
                >
                  Copier
                </button>
              </div>
            </div>
          )}

          <button
            onClick={onStart}
            className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 transition-colors"
          >
            Lancer la simulation
          </button>
        </div>
      </motion.div>
    </div>
  );
};



// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function CommercialPage() {
  const {
    engine, loadSimulation, loadBuilderScenario, restartSimulation,
    phase, scenario, openPanels, toggleFloatingPanel,
    showDebrief, closeDebrief, toggleShowDebrief,
    currentSolution, completedActions, applyAction, commercialLive,
    commercialLiveInit, commercialLiveSetConnected, commercialLiveReceiveEvent,
    commercialLiveAdvancePhase, commercialLiveGetDebriefData, commercialLiveReset,
  } = usePesa(); 
  

  const { slug, token }    = useParams();
  const navigate    = useNavigate();
  const V           = useVocab(scenario);
  const { theme, themeKey, toggle: toggleTheme } = useCommercialTheme(scenario);

  const [loading, setLoading]               = useState(true);
  const [presentationMode, setPresentationMode] = useState('solo');
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [leftCollapsed, setLeftCollapsed]   = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [pulsingPanels, setPulsingPanels]   = useState({});
  const [showPitch, setShowPitch] = useState(true);
  const [liveLink, setLiveLink] = useState(null);  
  const [clientSnapshot, setClientSnapshot] = useState(null);
  const [activeMedia, setActiveMedia] = useState(null);
  const [lastCorpusQuote, setLastCorpusQuote] = useState(null);
  const prevCountRef = useRef(0);
  const hasSentInitialSnapshot = useRef(false);
  const prevPhaseRef = useRef(null);

  const completedArr = completedActions instanceof Set ? [...completedActions] : [];
  const isPulsing = p => !!pulsingPanels[p];
  const progress = useMemo(() => {
    if (!scenario?.steps?.length) return 0;
    const total   = scenario.steps.length;
    const current = engine?.state?.currentStepIndex || 0;
    return Math.min(100, Math.round((current / Math.max(total - 1, 1)) * 100));
  }, [scenario, engine?.state?.currentStepIndex]);

  const liveToken = commercialLive.token;
  

  const {
    connected,
    clientEvents,
    lastEvent,
    postAction,
    postPhaseAdvance,
    closeSession,
    markEventsCompiled,
  } = useCommercialStream({
    token: liveToken,
    role:  commercialLive.role || presentationMode,
  });

  const handleGenerateLiveToken = useCallback(() => {
    const currentScenario = usePesa.getState().scenario;
    if (!currentScenario) {
      toast.error('Aucun scénario à partager.');
      return;
    }
    const token = Math.random().toString(36).substring(2, 10);
    const link = `${window.location.origin}/commercial-sales/live/${token}`;
    localStorage.setItem(`live_session_${token}`, JSON.stringify(currentScenario));
    hasSentInitialSnapshot.current = false;   // <-- réinitialisation
    commercialLiveInit({ token, role: 'seller' });
    setLiveLink(link);
  }, [commercialLiveInit]);

  const handleAdvancePhase = async () => {
    commercialLiveAdvancePhase();

    if (commercialLive.enabled && commercialLive.role === 'seller') {
      const state = engine.state;
      const currentStep = engine.getCurrentStep();
      const currentPhase = state.currentPhase;

      const snapshot = {
        currentStepIndex: state.currentStepIndex,
        currentPhaseIndex: state.currentPhaseIndex,
        clientNarrative: currentPhase?.clientNarrative || '',
        clientQuestion: currentPhase?.clientQuestion || '',
        clientActions: currentPhase?.clientActions || [],
        advanceLabel: currentPhase?.advanceLabel || '',
        activeMedia: activeMedia || null,
        isFinished: state.phase === 'finished',
        debriefSummary: state.phase === 'finished'
          ? (scenario?.solutions?.[0]?.interpretation?.shortSummary || 'Simulation terminée.')
          : null,
        // Nouveaux champs
        theory: scenario?.initialState?.theory,
        metadata: scenario?.metadata,
        stepIndex: state.currentStepIndex,
        totalSteps: scenario?.steps?.length || 0,
        stepLabels: scenario?.steps?.map(s => s.clientTitle || s.title) || [],
        probability: engine?.state?.parameters?.probabilite_accord,
        sessionTitle: scenario?.title,
        participants: 'Philippe Lambert, Marguerite Dubois',
        phaseKey: currentPhase?.title || currentPhase?.id,
        currentCorpusQuote: lastCorpusQuote,
      };

      await postAction({
        type: 'STATE_UPDATE',
        state: snapshot,
        currentStepIndex: state.currentStepIndex,
        currentPhaseIndex: state.currentPhaseIndex,
      });
    }
  };

  const handleCloseSession = async () => {
    hasSentInitialSnapshot.current = false
    await closeSession();
    commercialLiveReset();
    

  };

  const sendSnapshot = useCallback((mediaOverride) => {
    if (commercialLive.role !== 'seller' || !engine?.state) return;
    const state = engine.state;
    const currentPhase = state.currentPhase;
    const step = engine.getCurrentStep();
    
    const snapshot = {
    currentStepIndex: state.currentStepIndex,
    currentPhaseIndex: state.currentPhaseIndex,
    clientNarrative: currentPhase?.clientNarrative || '',
    clientQuestion: currentPhase?.clientQuestion || '',
    clientActions: currentPhase?.clientActions || [],
    advanceLabel: currentPhase?.advanceLabel || '',
    activeMedia: mediaOverride || activeMedia || null,
    isFinished: state.phase === 'finished',
    debriefSummary: state.phase === 'finished'
      ? (scenario?.solutions?.[0]?.interpretation?.shortSummary || 'Simulation terminée.')
      : null,
    // Nouveaux champs
    theory: scenario?.initialState?.theory,
    metadata: scenario?.metadata,
    stepIndex: state.currentStepIndex,
    totalSteps: scenario?.steps?.length || 0,
    stepLabels: scenario?.steps?.map(s => s.clientTitle || s.title) || [],
    probability: engine?.state?.parameters?.probabilite_accord,
    sessionTitle: scenario?.title,
    participants: 'Philippe Lambert, Marguerite Dubois',
    phaseKey: currentPhase?.title || currentPhase?.id,
    currentCorpusQuote: lastCorpusQuote,
  };
    postAction({
      type: 'STATE_UPDATE',
      state: snapshot,
      currentStepIndex: state.currentStepIndex,
      currentPhaseIndex: state.currentPhaseIndex,
    });
  }, [engine, activeMedia, postAction]);

  useEffect(() => {
    let isMounted = true;

    // 1. Session live prioritaire
    if (token) {
  // Client live : récupérer l'état initial depuis le serveur via l'API
  const fetchInitialState = async () => {
    try {
      const { data } = await api.get(`/commercial/session/${token}/state`);
      if (isMounted) {
        if (data.currentSnapshot) {
          setClientSnapshot(data.currentSnapshot);
        }
        commercialLiveInit({ token, role: 'client' });
        setPresentationMode('live');
        setShowPitch(false);
        setLoading(false);
      }
    } catch (err) {
      if (isMounted) {
        toast.error('Session live introuvable ou expirée.');
        navigate('/dashboard');
      }
    }
  };
  fetchInitialState();
  return;
}

    // 2. Mode preview (slug === 'preview')
    if (slug === 'preview') {
      if (isMounted) setLoading(false);
      return;
    }

    // 3. Chargement d'un scénario via l'API
    if (slug) {
      api.get(`/cases/generic/${slug}`)
        .then(res => {
          if (isMounted) {
            loadSimulation(res.data);
            setLoading(false);
          }
        })
        .catch(() => {
          if (isMounted) {
            toast.error("Impossible de charger la simulation.");
            navigate('/dashboard');
          }
        });
      return;
    }

    // 4. Aucun paramètre : si un scénario est déjà présent dans le store, on le garde
    if (engine?.case) {
      if (isMounted) setLoading(false);
      return;
    }

    // 5. Sinon, retour au tableau de bord
    if (isMounted) navigate('/dashboard');

    return () => { isMounted = false; };
  }, [slug, token]);

  useEffect(() => {
    if (engine?.case) {
      if (loading) setLoading(false);
    }
  }, [engine?.case]);

   useEffect(() => {
    if (!showPitch && engine?.state && commercialLive.role === 'seller' && !hasSentInitialSnapshot.current) {
      const sendInitialSnapshot = async () => {
        const state = engine.state;
        const currentPhase = state.currentPhase;
        const snapshot = {
          currentStepIndex: state.currentStepIndex,
          currentPhaseIndex: state.currentPhaseIndex,
          clientNarrative: currentPhase?.clientNarrative || '',
          clientQuestion: currentPhase?.clientQuestion || '',
          clientActions: currentPhase?.clientActions || [],
          advanceLabel: currentPhase?.advanceLabel || '',
          activeMedia: activeMedia || null,
          isFinished: state.phase === 'finished',
          debriefSummary: state.phase === 'finished'
            ? (scenario?.solutions?.[0]?.interpretation?.shortSummary || 'Simulation terminée.')
            : null,
          // Nouveaux champs
          theory: scenario?.initialState?.theory,
          metadata: scenario?.metadata,
          stepIndex: state.currentStepIndex,
          totalSteps: scenario?.steps?.length || 0,
          stepLabels: scenario?.steps?.map(s => s.clientTitle || s.title) || [],
          probability: engine?.state?.parameters?.probabilite_accord,
          sessionTitle: scenario?.title,
          participants: 'Philippe Lambert, Marguerite Dubois',
          phaseKey: currentPhase?.title || currentPhase?.id,
          currentCorpusQuote: lastCorpusQuote,
        };
        await postAction({
          type: 'STATE_UPDATE',
          state: snapshot,
          currentStepIndex: state.currentStepIndex,
          currentPhaseIndex: state.currentPhaseIndex,
        });
        hasSentInitialSnapshot.current = true;
      };
      sendInitialSnapshot();
    }
  }, [showPitch, engine?.state, commercialLive.role, postAction]);
 
  // Synchroniser la connexion SSE dans le store
  useEffect(() => {
    commercialLiveSetConnected(connected);
  }, [connected]);
 
  // Relayer les events client reçus via SSE dans le store
  useEffect(() => {
    if (lastEvent && (
      lastEvent.type === EVENT_TYPES.CLIENT_CHOICE ||
      lastEvent.type === EVENT_TYPES.CLIENT_INPUT
    )) {
      console.log('✅ Événement client traité côté vendeur', lastEvent);
      commercialLiveReceiveEvent(lastEvent);

      // Appliquer immédiatement l'effet sur le moteur du vendeur
      if (commercialLive.role === 'seller' && lastEvent.effect) {
        usePesa.setState(state => {
          if (!state.engine) return state;
          const newParameters = { ...state.engine.state.parameters };
          let changed = false;
          Object.entries(lastEvent.effect).forEach(([key, delta]) => {
            if (typeof newParameters[key] === 'number') {
              const newVal = Math.max(0, Math.min(100, newParameters[key] + delta));
              if (newVal !== newParameters[key]) {
                newParameters[key] = newVal;
                changed = true;
              }
            }
          });
          if (!changed) return state;
          return {
            engine: {
              ...state.engine,
              state: {
                ...state.engine.state,
                parameters: newParameters,
              },
            },
          };
        });
      }
      usePesa.getState().commercialLiveCompileEvents();
    }

    // Avance de phase pour le client
    if (lastEvent?.type === EVENT_TYPES.PHASE_ADVANCE && commercialLive.role === 'client') {
      engine?.advancePhase();
    }
    
  }, [lastEvent, commercialLive.role, engine]);
  

  useEffect(() => {
    if (completedArr.length > prevCountRef.current) {
      setPulsingPanels({ causal: Date.now(), prediction: Date.now(), counterfactuals: Date.now(), history: Date.now() });
      prevCountRef.current = completedArr.length;
      const t = setTimeout(() => setPulsingPanels({}), 2500);
      return () => clearTimeout(t);
    }
  }, [completedArr.length]);  
  
  useEffect(() => {
    if (presentationMode === 'live') { setLeftCollapsed(true); setRightCollapsed(true); }
    else { setLeftCollapsed(false); setRightCollapsed(false); }    
  }, [presentationMode]);

  useEffect(() => {
    if (lastEvent?.type === 'STATE_UPDATE' && commercialLive.role === 'client') {
      setClientSnapshot(lastEvent.state);
    }
  }, [lastEvent, commercialLive.role]); 

  useEffect(() => {
    if (commercialLive.role !== 'seller' || !engine?.state) return;
    const state = engine.state;
    const currentPhaseKey = `${state.currentStepIndex}-${state.currentPhaseIndex}`;
    if (prevPhaseRef.current === currentPhaseKey) return;
    prevPhaseRef.current = currentPhaseKey;

    const currentPhase = state.currentPhase;
    const snapshot = {
      currentStepIndex: state.currentStepIndex,
      currentPhaseIndex: state.currentPhaseIndex,
      clientNarrative: currentPhase?.clientNarrative || '',
      clientQuestion: currentPhase?.clientQuestion || '',
      clientActions: currentPhase?.clientActions || [],
      advanceLabel: currentPhase?.advanceLabel || '',
      activeMedia: activeMedia || null,
      isFinished: state.phase === 'finished',
      debriefSummary: state.phase === 'finished'
        ? (scenario?.solutions?.[0]?.interpretation?.shortSummary || 'Simulation terminée.')
        : null,
      // Nouveaux champs
      theory: scenario?.initialState?.theory,
      metadata: scenario?.metadata,
      stepIndex: state.currentStepIndex,
      totalSteps: scenario?.steps?.length || 0,
      stepLabels: scenario?.steps?.map(s => s.clientTitle || s.title) || [],
      probability: engine?.state?.parameters?.probabilite_accord,
      sessionTitle: scenario?.title,
      participants: 'Philippe Lambert, Marguerite Dubois',
      phaseKey: currentPhase?.title || currentPhase?.id,
      currentCorpusQuote: lastCorpusQuote,
    };
    postAction({
      type: 'STATE_UPDATE',
      state: snapshot,
      currentStepIndex: state.currentStepIndex,
      currentPhaseIndex: state.currentPhaseIndex,
    });
  }, [engine?.state?.currentStepIndex, engine?.state?.currentPhaseIndex, activeMedia]);

  if (commercialLive.role === 'client') {
    return (
      <div className="min-h-screen font-sans transition-colors duration-700"
        style={{ background: theme.bgHex, color: theme.ink }}>
        <div className="fixed inset-0 pointer-events-none z-0" style={{
          backgroundImage: `linear-gradient(${theme.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${theme.gridLine} 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }} />
        <div className="fixed inset-0 z-0 opacity-10 pointer-events-none">
          <PESAField />
        </div>
        <div className="fixed inset-0 z-10">
       <ClientView snapshot={clientSnapshot} postAction={postAction} theme={theme}/>
     </div>
      </div>
    );
  }

  if (loading || !scenario) return <LoadingScreen theme={theme} />;

  if (showPitch) {
    return (
      <PitchScreen
        theme={theme}
        V={V}
        presentationMode={presentationMode}
        setPresentationMode={setPresentationMode}
        onStart={() => setShowPitch(false)}
        liveLink={liveLink}
        setLiveLink={setLiveLink}
        onGenerateLiveToken={handleGenerateLiveToken}
        setClientSnapshot={setClientSnapshot}
      />
    );
  }

  if (!engine) return null;

  return (
    <div className="min-h-screen font-sans transition-colors duration-700"
      style={{ background: theme.bgHex, color: theme.ink }}>

      {/* Grille décorative */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{
        backgroundImage: `linear-gradient(${theme.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${theme.gridLine} 1px, transparent 1px)`,
        backgroundSize: '80px 80px',
      }} />
      <div className="fixed inset-0 z-0 opacity-10 pointer-events-none">
        <PESAField />
      </div>

      <div className="relative z-10 flex flex-col h-screen">
        <CommercialHeader
          scenario={scenario}
          phase={phase}
          progress={progress}
          theme={theme}
          themeKey={themeKey}
          onToggleTheme={toggleTheme}
          onRestart={restartSimulation}
          presentationMode={presentationMode}
          setPresentationMode={setPresentationMode}
          navigate={navigate}
        />

        <main className="flex-1 overflow-hidden relative"
          style={{
            display: 'grid',
            gridTemplateColumns: [
              leftCollapsed  ? '0px' : 'minmax(0,1fr)',
              'minmax(0,2.2fr)',
              rightCollapsed ? '0px' : 'minmax(0,1fr)',
            ].join(' '),
            transition: 'grid-template-columns 320ms cubic-bezier(0.4,0,0.2,1)',
          }}>

          {/* ── Colonne gauche ─────────────────────────────────────────────── */}
          <div className="flex flex-col gap-4 h-full overflow-hidden"
            style={{
              opacity:       leftCollapsed ? 0 : 1,
              pointerEvents: leftCollapsed ? 'none' : 'auto',
              transition:    'opacity 180ms ease',
              padding:       leftCollapsed ? 0 : '1.5rem',
              paddingRight:  leftCollapsed ? 0 : '0.75rem',
            }}>
            <div className="shrink-0 space-y-1">
              <div className="px-2 mb-3">
                <Mono style={{ color: `${theme.ink}22` }}>Panneaux</Mono>
              </div>
              {[
                { key: 'dynamics',        Icon: Activity,   label: V('dynamics').label        },
                { key: 'causal',          Icon: Network,    label: V('causality').label,       pulse: isPulsing('causal')          },
                { key: 'history',         Icon: History,    label: V('history').label,         pulse: isPulsing('history')         },
                { key: 'prediction',      Icon: TrendingUp, label: V('prediction').label,      pulse: isPulsing('prediction')      },
                { key: 'counterfactuals', Icon: GitBranch,  label: V('counterfactuals').label, pulse: isPulsing('counterfactuals') },
              ].map(({ key, Icon, label, pulse }) => (
                <NavButton key={key} active={openPanels[key]} icon={Icon} label={label}
                  onClick={() => toggleFloatingPanel(key)} pulsing={pulse} theme={theme} />
              ))}
            </div>

            {/* Outils */}
            <div className="shrink-0 space-y-1">
              <div className="px-2 mb-2">
                <Mono style={{ color: `${theme.ink}22` }}>Outils</Mono>
              </div>
              <button onClick={() => setFieldModalOpen(true)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border text-[9px] font-mono tracking-widest uppercase transition-all"
                style={{ borderColor: `${theme.ink}08`, color: `${theme.ink}30` }}
                onMouseEnter={e => { e.currentTarget.style.color = `${theme.ink}60`; e.currentTarget.style.borderColor = `${theme.ink}15`; }}
                onMouseLeave={e => { e.currentTarget.style.color = `${theme.ink}30`; e.currentTarget.style.borderColor = `${theme.ink}08`; }}>
                <Maximize2 size={11} />Champ Vivant
              </button>
              {phase === 'finished' && (
                <button onClick={() => toggleShowDebrief(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border text-[9px] font-mono tracking-widest uppercase transition-all"
                  style={{ borderColor: `${theme.accent}18`, color: `${theme.accent}55` }}
                  onMouseEnter={e => { e.currentTarget.style.color = theme.accent; e.currentTarget.style.borderColor = `${theme.accent}35`; }}
                  onMouseLeave={e => { e.currentTarget.style.color = `${theme.accent}55`; e.currentTarget.style.borderColor = `${theme.accent}18`; }}>
                  <FileText size={11} />Rapport final
                </button>
              )}
              {commercialLive.enabled && commercialLive.role === 'seller' && (
                <button
                  onClick={handleCloseSession}
                  className="px-3 py-1.5 rounded-lg text-xs font-mono tracking-widest uppercase transition-all"
                  style={{ background: '#ef444410', color: '#ef4444', border: '1px solid #ef444425' }}
                >
                  Terminer la session live
                </button>
              )}
            </div>

            {/* Panneaux ouverts */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1" style={{ scrollbarWidth: 'none' }}>
              <AnimatePresence mode="popLayout">
                {openPanels.prediction && (
                  <PanelCard key="pred" theme={theme}>
                    <SectionHeader label={V('prediction').label} icon={TrendingUp} theme={theme} />
                    <PredictionPanel theme={theme} V={V} />
                  </PanelCard>
                )}
                {openPanels.dynamics && (
                  <PanelCard key="dyna" theme={theme}>
                    <SectionHeader label={V('dynamics').label} icon={Activity} theme={theme} />
                    <DynamicsPanel theme={theme} V={V} />
                  </PanelCard>
                )}
                {openPanels.causal && (
                  <PanelCard key="caus" theme={theme}>
                    <SectionHeader label={V('causality').label} icon={Network} theme={theme} />
                    <CausalPanel theme={theme} V={V} />
                  </PanelCard>
                )}
                {openPanels.counterfactuals && phase === 'playing' && (
                  <PanelCard key="count" theme={theme}>
                    <SectionHeader label={V('counterfactuals').label} icon={GitBranch} theme={theme} />
                    <CounterfactualsPanel counterfactuals={engine?.counterfactuals || []} theme={theme} V={V} />
                  </PanelCard>
                )}
                {openPanels.history && (
                  <PanelCard key="hist" theme={theme}>
                    <SectionHeader label={V('history').label} icon={History} theme={theme} />
                    <HistoryPanel theme={theme} V={V} />
                  </PanelCard>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* ── Colonne centrale ───────────────────────────────────────────── */}
          <div className="flex flex-col overflow-hidden relative p-6 px-3">
            {/* Toggle gauche */}
            <button onClick={() => setLeftCollapsed(v => !v)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-5 h-12 rounded-r-xl border-y border-r backdrop-blur-xl transition-all"
              style={{
                background: leftCollapsed ? theme.accent : `${theme.ink}06`,
                borderColor: leftCollapsed ? `${theme.accent}50` : `${theme.ink}10`,
                color: leftCollapsed ? '#fff' : `${theme.ink}25`,
              }}>
              {leftCollapsed ? <PanelLeftOpen size={11} /> : <PanelLeftClose size={11} />}
            </button>

            {/* Toggle droite */}
            <button onClick={() => setRightCollapsed(v => !v)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-5 h-12 rounded-l-xl border-y border-l backdrop-blur-xl transition-all"
              style={{
                background: rightCollapsed ? theme.accent : `${theme.ink}06`,
                borderColor: rightCollapsed ? `${theme.accent}50` : `${theme.ink}10`,
                color: rightCollapsed ? '#fff' : `${theme.ink}25`,
              }}>
              {rightCollapsed ? <PanelRightOpen size={11} /> : <PanelRightClose size={11} />}
            </button>

            {/* Zone overlay */}
            <div className="relative flex-1 overflow-hidden mx-5"
              style={{
                borderRadius: '2.5rem',
                border: `1px solid ${theme.panelBorder}`,
                background: themeKey === 'light' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
                backdropFilter: 'blur(4px)',
                boxShadow: themeKey === 'light' ? '0 8px 40px rgba(0,0,0,0.05)' : '0 8px 60px rgba(0,0,0,0.4)',
              }}>
              
    
              <CommercialOverlay
                theme={theme}
                V={V}
                isLiveMode={presentationMode === 'live'}
                onAdvancePhase={handleAdvancePhase}
                postAction={postAction}
                commercialLiveGetDebriefData={commercialLiveGetDebriefData}
                onActiveMediaChange={(media) => setActiveMedia(media)}
                onSnapshot={sendSnapshot}
              />
  
            </div>
          </div>

          {/* ── Colonne droite ─────────────────────────────────────────────── */}
          <div className="flex flex-col gap-4 overflow-hidden"
            style={{
              opacity:       rightCollapsed ? 0 : 1,
              pointerEvents: rightCollapsed ? 'none' : 'auto',
              transition:    'opacity 180ms ease',
              padding:       rightCollapsed ? 0 : '1.5rem',
              paddingLeft:   rightCollapsed ? 0 : '0.75rem',
            }}>
            <div className="shrink-0">
              <ROIPanel theme={theme} V={V} scenario={scenario} />
            </div>
            <div className="shrink-0">
              <PanelCard theme={theme}>
                <SectionHeader label={V('currentSolution').label} icon={CheckCircle2} theme={theme} />
                <SolutionPanel theme={theme} V={V} />
              </PanelCard>
            </div>
          </div>

        </main>
      </div>

      <LiveFieldModal isOpen={fieldModalOpen} onClose={() => setFieldModalOpen(false)} />
      <Toaster position="bottom-right" />
      {showDebrief && typeof RapportEnquete !== 'undefined' && (
        <RapportEnquete onClose={closeDebrief} />
      )}
    </div>
  );
}